import { IntlShape } from 'react-intl';
import { daysUntilRelease } from '../../music24/promote/releases/releaseOrder';
import { PublishedPageKind } from './publishedPageKind';

/**
 * Where the release stands, in three words.
 *
 * A pre-save page counts down to the day; a release page counts up from it.
 * Without a date there is nothing to count, so the release page says the one
 * thing that is certainly true and the bio link says nothing at all.
 */
export function publishedStatusTag(
  intl: IntlShape,
  kind: PublishedPageKind,
  releaseDate: string | null
): string | undefined {
  if (kind === PublishedPageKind.bioLink) {
    return undefined;
  }

  if (releaseDate === null) {
    return kind === PublishedPageKind.presave
      ? intl.formatMessage({ id: 'app.sml.published.tag.presave_open', defaultMessage: 'Pre-save open' })
      : intl.formatMessage({ id: 'app.sml.published.tag.out_now', defaultMessage: 'Out now' });
  }

  // Calendar days, as the release page counts them ("20 days to go"), not 24-hour blocks (QA F18).
  const days = daysUntilRelease(releaseDate, new Date());
  if (days === null) {
    return undefined;
  }

  if (kind === PublishedPageKind.presave) {
    const daysToGo = Math.max(0, days);
    return daysToGo === 0
      ? intl.formatMessage({ id: 'app.sml.published.tag.releases_today', defaultMessage: 'Releases today' })
      : intl.formatMessage(
          {
            id: 'app.sml.published.tag.to_release',
            defaultMessage: '{count, plural, one {# day to release} other {# days to release}}',
          },
          { count: daysToGo }
        );
  }

  const daysOut = Math.max(0, -days);
  return daysOut === 0
    ? intl.formatMessage({ id: 'app.sml.published.tag.out_now', defaultMessage: 'Out now' })
    : intl.formatMessage(
        {
          id: 'app.sml.published.tag.out_days',
          defaultMessage: '{count, plural, one {out # day} other {out # days}}',
        },
        { count: daysOut }
      );
}
